import React, { useMemo } from "react";
import { BarChart3, Layers, PieChart, TrendingUp } from "lucide-react";
import type { PositionData } from "@crypto-trading/common/browser";
import { MetricCard } from "@/components/ui/MetricCard";
import { useStats } from "@/hooks/useStats";

const usd = (value: number) =>
  `${value < 0 ? "-" : ""}$${Math.abs(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const num = (value: string | number | undefined | null) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

interface TradeStatsCardsProps {
  userId: string;
  positions: PositionData[];
  /** 4 in a row (desktop) or 2 × 2 (mobile). */
  layout?: "row" | "grid";
}

/**
 * Total Positions, Total Exposure (position value at mark), Unrealized PnL
 * (open positions shown) and Realized PnL (all closed positions, from stats).
 */
export const TradeStatsCards: React.FC<TradeStatsCardsProps> = ({
  userId,
  positions,
  layout = "row",
}) => {
  const { data: stats } = useStats(userId);
  const { exposure, unrealized } = useMemo(() => {
    let exposure = 0;
    let unrealized = 0;
    for (const p of positions) {
      const price = num(p.markPrice) || num(p.entryPrice);
      exposure += Math.abs(num(p.amount) * price) || num(p.usdAmount) * (num(p.leverage) || 1);
      unrealized += num(p.pnl);
    }
    return { exposure, unrealized };
  }, [positions]);
  const realized = stats?.totalPnl ?? 0;
  const size = layout === "grid" ? "sm" : "md";

  return (
    <div className={layout === "row" ? "grid grid-cols-4 gap-4" : "grid grid-cols-2 gap-2"}>
      <MetricCard size={size} tone="green" icon={<Layers />} label="Total Positions" value={positions.length} />
      <MetricCard size={size} tone="blue" icon={<PieChart />} label="Total Exposure" value={usd(exposure)} />
      <MetricCard
        size={size}
        tone="purple"
        icon={<TrendingUp />}
        label="Unrealized PnL"
        value={<span className={unrealized < 0 ? "text-[#f87171]" : unrealized > 0 ? "text-[#2dcc89]" : ""}>{usd(unrealized)}</span>}
      />
      <MetricCard
        size={size}
        tone="indigo"
        icon={<BarChart3 />}
        label="Realized PnL"
        value={<span className={realized < 0 ? "text-[#f87171]" : realized > 0 ? "text-[#2dcc89]" : ""}>{usd(realized)}</span>}
      />
    </div>
  );
};
